import PageGlow from "../components/PageGlow";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Sparkles, ShieldAlert, Bookmark, X, Info } from "lucide-react";
import { api, formatApiError, streamAi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import ProtectedRoute from "../components/ProtectedRoute";
import LocationInput from "../components/LocationInput";
import ShareButtons from "../components/ShareButtons";
import { SITE_URL } from "../lib/api";

const EMPTY = {
  title: "", category: "", location: "", workplace: "On-site", job_type: "Full-time",
  salary_min: "", salary_max: "", salary_period: "year", show_salary: true,
  description: "", company_name: "", company_website: "", company_description: "",
  application_email: "", apply_method: "platform", external_apply_url: "", urgent: false, tier: "free",
  partner_distribution: false,
  screening_questions: [], boost_locations: [],
};

const TIER_OPTIONS = [
  { id: "free", name: "Free", note: "One live job at a time. Live for 7 days." },
  { id: "standard", name: "Standard", note: "Listed above free jobs for 30 days." },
  { id: "featured", name: "Featured", note: "Top of search, homepage placement, featured badge." },
];

function PostJobForm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefill = useLocation().state?.prefill;
  const [meta, setMeta] = useState({ categories: [], job_types: [], workplaces: [] });
  const [form, setForm] = useState(EMPTY);
  const [templates, setTemplates] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoChecking(true);
    setPromoError("");
    setPromo(null);
    try {
      const r = await api.post("/payments/validate-promo", { code: promoCode.trim() });
      if (r.data.valid) setPromo({ code: r.data.code, percent: r.data.percent });
      else setPromoError(r.data.reason || "That code isn't valid");
    } catch (err) {
      setPromoError(formatApiError(err, "Could not check that code"));
    } finally {
      setPromoChecking(false);
    }
  };

  const [subInfo, setSubInfo] = useState(null);
  const tierPrice = (id) => pricing?.[id]?.price ?? (id === "standard" ? 20 : id === "featured" ? 49 : 0);
  const tierCompare = (id) => pricing?.[id]?.compare_at;
  const urgentPrice = pricing?.urgent?.price ?? 9;
  const basePrice = (form.tier === "free" ? 0 : tierPrice(form.tier)) + (form.urgent ? urgentPrice : 0);
  const finalPrice = promo ? Math.round(basePrice * (100 - promo.percent)) / 100 : basePrice;
  // a plan credit covers a standard listing; a pre-bought urgent label covers the add-on
  const planCovered = form.tier === "standard" && (subInfo?.credits_remaining || 0) > 0;
  const urgentCovered = !form.urgent || (subInfo?.urgent_credits || 0) > 0;
  const fullyCovered = (form.tier === "free" || planCovered) && urgentCovered;
  const planUsed = planCovered || (form.urgent && (subInfo?.urgent_credits || 0) > 0);
  const duePrice = promo
    ? Math.round(((planCovered ? 0 : tierPrice(form.tier)) + (form.urgent && !urgentCovered ? urgentPrice : 0)) * (100 - promo.percent)) / 100
    : (planCovered ? 0 : tierPrice(form.tier)) + (form.urgent && !urgentCovered ? urgentPrice : 0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [aiWriting, setAiWriting] = useState(false);
  const descRef = useRef(null);

  const loadTemplates = () => {
    api.get("/employer/templates").then((r) => setTemplates(r.data)).catch(() => {});
  };

  const applyMark = (type) => {
    const ta = descRef.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e, value } = ta;
    let next, pos;
    if (type === "bold") {
      const sel = value.slice(s, e) || "bold text";
      next = value.slice(0, s) + `**${sel}**` + value.slice(e);
      pos = s + 2 + sel.length + 2;
    } else {
      const marker = type === "bullet" ? "- " : "### ";
      const lineStart = value.lastIndexOf("\n", s - 1) + 1;
      next = value.slice(0, lineStart) + marker + value.slice(lineStart);
      pos = s + marker.length;
    }
    set("description", next);
    requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(pos, pos); });
  };

  useEffect(() => {
    api.get("/jobs/meta").then((res) => setMeta(res.data)).catch(() => {});
    api.get("/pricing").then((res) => setPricing(res.data)).catch(() => {});
    api.get("/subscriptions/my").then((res) => setSubInfo(res.data.subscription)).catch(() => {});
    loadTemplates();
  }, []);

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({
        ...f,
        ...Object.fromEntries(Object.entries(EMPTY).map(([k]) => [k, prefill[k] ?? f[k]])),
        urgent: false,
        tier: "free",
      }));
      if (prefill.title) toast.success(`Details copied from "${prefill.title}". Review and publish when ready.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        company_name: f.company_name || user.company_name || "",
        company_website: f.company_website || user.company_website || "",
        company_description: f.company_description || user.company_description || "",
        application_email: f.application_email || user.email || "",
        tier: ["free", "standard", "featured"].includes(searchParams.get("tier")) ? searchParams.get("tier") : f.tier,
      }));
    }
  }, [user, searchParams]);

  const saveTemplate = async () => {
    if (!form.title.trim()) {
      toast.error("Add a job title first");
      return;
    }
    const name = window.prompt("Name this template (e.g. Weekend warehouse operative):", form.title);
    if (!name) return;
    try {
      await api.post("/employer/templates", { name, data: form });
      toast.success("Template saved. Pick it from the dropdown next time");
      loadTemplates();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setForm((f) => ({ ...f, ...t.data }));
    toast.success(`Template "${t.name}" loaded`);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // per-user draft: leaving mid-post never loses progress; cleared on successful submit
  const draftKey = user?.id ? `ht_postjob_draft_${user.id}` : null;
  const [draftReady, setDraftReady] = useState(false);

  useEffect(() => {
    if (!draftKey || prefill || draftReady) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw) || {};
        const dirty = Object.keys(EMPTY).some((k) => JSON.stringify(d[k] ?? EMPTY[k]) !== JSON.stringify(EMPTY[k]));
        if (dirty) {
          setForm((f) => ({ ...f, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, d[k] ?? f[k]])) }));
          toast.info("Draft restored, carry on where you left off.");
        }
      }
    } catch { /* draft restore is best-effort */ }
    setDraftReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, prefill, draftReady]);

  useEffect(() => {
    if (!draftKey || !draftReady) return undefined;
    const t = setTimeout(() => {
      try {
        const dirty = Object.keys(EMPTY).some((k) => JSON.stringify(form[k]) !== JSON.stringify(EMPTY[k]));
        if (dirty) localStorage.setItem(draftKey, JSON.stringify(form));
        else localStorage.removeItem(draftKey);
      } catch { /* draft save is best-effort */ }
    }, 600);
    return () => clearTimeout(t);
  }, [form, draftKey, draftReady]);

  const draftWithAi = async () => {
    if (!form.title.trim()) {
      toast.error("Add a job title first. The AI needs at least that.");
      return;
    }
    setAiWriting(true);
    set("description", "");
    try {
      await streamAi("/ai/job-description", {
        title: form.title,
        company_name: form.company_name,
        location: form.location,
        category: form.category,
        notes: form.description,
      }, (text) => set("description", text));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAiWriting(false);
    }
  };

  const [boostInput, setBoostInput] = useState("");
  const [boostLoading, setBoostLoading] = useState(false);
  const manualBoost = useRef([]);        // areas the employer added by hand, never auto-removed
  const lastBoostLocation = useRef("");  // location the current suggestions were generated from

  const suggestBoost = async ({ silent = false, replaceAuto = false } = {}) => {
    if (!form.location.trim()) return;
    setBoostLoading(true);
    try {
      const r = await api.get("/employer/location-boost", { params: { location: form.location } });
      const sugg = r.data.suggestions || [];
      if (replaceAuto) {
        // location changed: regenerate from the new location, keep hand-added areas
        const manual = manualBoost.current.filter((m) => !sugg.includes(m));
        set("boost_locations", [...manual, ...sugg.filter((s) => !manual.includes(s))].slice(0, 10));
        lastBoostLocation.current = form.location.trim();
      } else {
        const fresh = sugg.filter((s) => !form.boost_locations.includes(s));
        set("boost_locations", [...form.boost_locations, ...fresh].slice(0, 10));
        lastBoostLocation.current = form.location.trim();
        if (!fresh.length && !silent) toast.info("No nearby areas found for that location, add them yourself below.");
      }
    } catch (err) {
      if (!silent) toast.error(formatApiError(err));
    } finally {
      setBoostLoading(false);
    }
  };

  const addBoost = () => {
    const v = boostInput.trim();
    if (!v) return;
    if (form.boost_locations.includes(v)) { setBoostInput(""); return; }
    if (form.boost_locations.length >= 10) { toast.error("Up to 10 boosted areas"); return; }
    if (!manualBoost.current.includes(v)) manualBoost.current = [...manualBoost.current, v];
    set("boost_locations", [...form.boost_locations, v]);
    setBoostInput("");
  };

  // location changed → regenerate boost areas for the new location (hand-added ones are kept)
  useEffect(() => {
    if (form.tier === "free" && !form.urgent) return undefined;
    const loc = form.location.trim();
    if (!loc || loc === lastBoostLocation.current) return undefined;
    const t = setTimeout(() => {
      if (loc !== lastBoostLocation.current) suggestBoost({ silent: true, replaceAuto: true });
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.location, form.tier, form.urgent]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    if (form.apply_method === "external" && !form.external_apply_url.trim().startsWith("http")) {
      toast.error("Add the full link to your application page (starting with https://)");
      setSubmitting(false);
      return;
    }
    try {
      const payload = {
        ...form,
        salary_min: form.salary_min === "" ? null : Number(form.salary_min),
        salary_max: form.salary_max === "" ? null : Number(form.salary_max),
      };
      delete payload.promo_code;
      const res = await api.post("/employer/jobs", payload);
      if (draftKey) { try { localStorage.removeItem(draftKey); } catch { /* draft clear is best-effort */ } }
      if (res.data.credit_used) toast.success("One plan credit used for this listing.");
      if (res.data.requires_payment) {
        const checkout = await api.post("/payments/checkout", {
          job_id: res.data.job.id,
          origin_url: window.location.origin,
          promo_code: promo ? promo.code : "",
        });
        if (checkout.data.free) {
          setDone(res.data.job);
          toast.success("Submitted for review. No payment needed.");
          return;
        }
        window.location.href = checkout.data.checkout_url;
        return;
      }
      setDone(res.data.job);
      toast.dismiss();
    } catch (err) {
      toast.error(formatApiError(err, "Could not submit your job"));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="job-submitted">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">Your job is in the queue.</h1>
        <p className="mt-4 text-zinc-600 leading-relaxed">
        A member of our team will read <span className="font-medium text-zinc-900">"{done.title}"</span> before it goes live. Usually within one working day. We'll keep its status updated in your dashboard.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => navigate("/dashboard")} data-testid="go-to-dashboard"
            className="bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-zinc-800 transition-colors duration-200">
            Go to my dashboard
          </button>
          <button onClick={() => { setForm(EMPTY); setDone(null); }} data-testid="post-another"
            className="border border-zinc-300 text-sm font-semibold px-6 py-3 rounded-md hover:border-zinc-500 transition-colors duration-200">
            Post another job
          </button>
        </div>
        {done.slug && (
          <div className="mt-10 border border-zinc-200 rounded-md p-6 bg-white" data-testid="share-listing-prompt">
            <p className="font-display font-bold text-zinc-950">Share your listing</p>
            <p className="text-xs text-zinc-500 mt-1 mb-4 leading-relaxed">
              Once it's live, this link shows a branded preview card, role, salary and your logo, on LinkedIn, WhatsApp and more.
            </p>
            <div className="flex justify-center">
              <ShareButtons path={`/jobs/${done.slug}`} title={`${done.title} at ${done.company_name || "our team"}`}
                testid="postjob" shareUrl={`${SITE_URL}/api/share/jobs/${done.slug}`} />
            </div>
          </div>
        )}
      </main>
    );
  }

  return (
    <main className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="post-job-page">
      <PageGlow subtle />
      <Seo title="Post a Job Free in the UK | HiredToday" description="Post a UK job free, or choose Standard or Featured for more reach." path="/post-a-job" />
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">Employers</p>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-black text-zinc-950">Post a free job</h1>

      {user && user.verification_status !== "verified" && !user.parent_employer_id && (
        <div className="mt-6 glass rounded-lg p-5 flex items-start gap-3 border-l-4 border-l-amber-400" data-testid="verification-banner">
          <ShieldAlert className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-zinc-950">Your company isn't verified yet</p>
            <p className="text-sm text-zinc-600 mt-1">
              You can prepare the job now, but it can only go live once we've verified your company, usually the same day.{" "}
              <button type="button" onClick={() => navigate("/dashboard")} data-testid="banner-go-verify"
                className="text-blue-700 font-medium hover:text-blue-900">
                Submit verification from your dashboard
              </button>
            </p>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-10 space-y-10" data-testid="post-job-form">
        {templates.length > 0 && (
          <div className="border border-zinc-200 rounded-md p-4 bg-zinc-50/60 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="template-bar">
            <p className="text-sm font-medium text-zinc-700 flex-1">Start from one of your saved templates:</p>
            <select defaultValue="" onChange={(e) => { applyTemplate(e.target.value); e.target.value = ""; }} data-testid="template-picker"
              className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="" disabled>Choose a template…</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
        <section>
          <h2 className="font-display text-xl font-bold text-zinc-950 mb-5">The job</h2>
          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Job title *</label>
              <input required data-testid="job-title-input" value={form.title} onChange={(e) => set("title", e.target.value)}
                placeholder="e.g. Class 1 HGV Driver"
                className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Category *</label>
                <select required data-testid="job-category-select" value={form.category} onChange={(e) => set("category", e.target.value)}
                  className="w-full h-12 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600">
                  <option value="">Choose a category</option>
                  {form.category && !meta.categories.includes(form.category) && (
                    <option value={form.category}>{form.category}</option>
                  )}
                  {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Location *</label>
                <LocationInput value={form.location} onChange={(v) => set("location", v)} testid="job-location-input" placeholder="e.g. Leeds, West Yorkshire" />
                <span className="group relative inline-flex mt-1.5" data-testid="location-hint">
                  <Info className="h-3.5 w-3.5 text-zinc-400 cursor-help" />
                  <span className="pointer-events-none absolute bottom-full left-0 z-20 mb-1.5 w-64 rounded-md bg-zinc-950 px-3 py-2 text-[11px] leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">Start typing and pick a suggestion. This is what Google Jobs shows candidates.</span>
                </span>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Job type</label>
                <select data-testid="job-type-select" value={form.job_type} onChange={(e) => set("job_type", e.target.value)}
                  className="w-full h-12 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600">
                  {meta.job_types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Workplace</label>
                <select data-testid="job-workplace-select" value={form.workplace} onChange={(e) => set("workplace", e.target.value)}
                  className="w-full h-12 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600">
                  {meta.workplaces.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-zinc-700">Salary</label>
                <label className="flex items-center gap-2 text-sm text-zinc-500">
                  <input type="checkbox" data-testid="show-salary-toggle" checked={form.show_salary}
                    onChange={(e) => set("show_salary", e.target.checked)} className="h-4 w-4 accent-zinc-900" />
                  Show salary on listing
                </label>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <input type="number" min="0" data-testid="salary-min-input" value={form.salary_min} onChange={(e) => set("salary_min", e.target.value)}
                  placeholder="From £"
                  className="h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <input type="number" min="0" data-testid="salary-max-input" value={form.salary_max} onChange={(e) => set("salary_max", e.target.value)}
                  placeholder="To £"
                  className="h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <select data-testid="salary-period-select" value={form.salary_period} onChange={(e) => set("salary_period", e.target.value)}
                  className="h-12 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 col-span-2 sm:col-span-1">
                  <option value="year">per year</option>
                  <option value="month">per month</option>
                  <option value="week">per week</option>
                  <option value="day">per day</option>
                  <option value="hour">per hour</option>
                </select>
              </div>
              <span className="group relative inline-flex mt-2" data-testid="salary-hint">
                <Info className="h-3.5 w-3.5 text-zinc-400 cursor-help" />
                <span className="pointer-events-none absolute bottom-full left-0 z-20 mb-1.5 w-64 rounded-md bg-zinc-950 px-3 py-2 text-[11px] leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">Listings with a salary get noticeably more applications. They also rank better on Google Jobs.</span>
              </span>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-zinc-700">Job description *</label>
                <button type="button" onClick={draftWithAi} disabled={aiWriting} data-testid="ai-draft-button"
                  className="ai-btn inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-50">
                  <Sparkles className={`h-3.5 w-3.5 ai-spark ${aiWriting ? "animate-pulse" : ""}`} />
                  {aiWriting ? "Writing…" : "Draft it for me"}
                </button>
              </div>
              <div className="flex gap-1.5 mb-1.5" data-testid="desc-toolbar">
                <button type="button" onClick={() => applyMark("bold")} data-testid="desc-bold"
                  className="h-8 px-3 border border-zinc-300 rounded-md text-xs font-bold text-zinc-700 hover:border-zinc-500 transition-colors duration-200">B</button>
                <button type="button" onClick={() => applyMark("bullet")} data-testid="desc-bullet"
                  className="h-8 px-3 border border-zinc-300 rounded-md text-xs font-semibold text-zinc-700 hover:border-zinc-500 transition-colors duration-200">• Bullet list</button>
                <button type="button" onClick={() => applyMark("heading")} data-testid="desc-heading"
                  className="h-8 px-3 border border-zinc-300 rounded-md text-xs font-semibold text-zinc-700 hover:border-zinc-500 transition-colors duration-200">Heading</button>
                <span className="self-center text-xs text-zinc-400 ml-1 hidden sm:inline">Formats your description. No code needed</span>
              </div>
              <textarea required ref={descRef} data-testid="job-description-input" value={form.description} onChange={(e) => set("description", e.target.value)}
                rows={9}
                placeholder={"What the role involves day to day, what you're looking for, and anything that makes it worth having.\n\nShifts, hours, benefits, training: put it here."}
                className="w-full px-4 py-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 resize-y leading-relaxed" />
            </div>
          </div>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-zinc-950 mb-5">Your company</h2>
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Company name *</label>
                <input required data-testid="company-name-input" value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
                  className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Company website</label>
                <input data-testid="company-website-input" value={form.company_website} onChange={(e) => set("company_website", e.target.value)}
                  placeholder="https://"
                  className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">About the company</label>
              <textarea data-testid="company-description-input" value={form.company_description} onChange={(e) => set("company_description", e.target.value)}
                rows={4} placeholder="A couple of honest sentences about who you are."
                className="w-full px-4 py-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 resize-y" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Contact email for this listing</label>
              <input type="email" data-testid="application-email-input" value={form.application_email} onChange={(e) => set("application_email", e.target.value)}
                className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-2">How candidates apply</label>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="apply-method-selector">
                {[
                  { id: "platform", label: "On HiredToday", note: "Applications land in your dashboard and you get an email for each one." },
                  { id: "external", label: "On HiredToday + your website", note: "Candidates apply here as usual, and can also choose your careers page or ATS link." },
                ].map((m) => (
                  <button type="button" key={m.id} onClick={() => set("apply_method", m.id)} data-testid={`apply-method-${m.id}`}
                    className={`text-left rounded-md p-4 border-2 transition-colors duration-200 ${
                      form.apply_method === m.id ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
                    <p className="text-sm font-semibold text-zinc-950">{m.label}</p>
                    <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{m.note}</p>
                  </button>
                ))}
              </div>
              {form.apply_method === "external" && (
                <div className="mt-3">
                  <input data-testid="external-apply-url" value={form.external_apply_url} onChange={(e) => set("external_apply_url", e.target.value)}
                    placeholder="https://yourcompany.com/careers/apply/…"
                    className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  <p className="text-xs text-zinc-400 mt-1.5">We still count every click-through, so you can see how the listing performs.</p>
                </div>
              )}
            </div>
            {form.apply_method === "platform" && (
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-2">Screening questions (optional, up to 5)</label>
                <div className="space-y-2" data-testid="screening-questions">
                  {form.screening_questions.map((q, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input data-testid={`screening-q-${i}`} value={q} maxLength={200}
                        onChange={(e) => set("screening_questions", form.screening_questions.map((x, xi) => (xi === i ? e.target.value : x)))}
                        placeholder="e.g. Do you have the right to work in the UK?"
                        className="flex-1 h-11 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                      <button type="button" onClick={() => set("screening_questions", form.screening_questions.filter((_, xi) => xi !== i))}
                        data-testid={`screening-q-remove-${i}`} aria-label="Remove question"
                        className="p-2 text-zinc-400 hover:text-red-600 transition-colors duration-200">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {form.screening_questions.length < 5 && (
                  <button type="button" onClick={() => set("screening_questions", [...form.screening_questions, ""])}
                    data-testid="screening-q-add"
                    className="mt-2 text-sm font-semibold text-blue-700 hover:text-blue-900 transition-colors duration-200">
                    + Add a question
                  </button>
                )}
                <p className="text-xs text-zinc-400 mt-1.5">Candidates answer these when they apply. Their answers arrive with each application.</p>
              </div>
            )}
          </div>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-zinc-950 mb-5">Choose your listing</h2>
          {subInfo && ["active", "past_due", "cancelling"].includes(subInfo.status) && (
            <div className="mb-4 rounded-md border border-violet-200 bg-violet-50 px-4 py-3 text-xs text-violet-800 leading-relaxed" data-testid="plan-coverage-note">
              <span className="font-bold">Your {subInfo.plan_name} plan:</span> Standard listings are included, {subInfo.credits_remaining} of {subInfo.credits_total} credits left this month.
              Featured upgrades and the Urgent label are always one-off extras.
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-4" data-testid="tier-selector">
            {TIER_OPTIONS.map((t) => (
              <button type="button" key={t.id} onClick={() => set("tier", t.id)} data-testid={`tier-option-${t.id}`}
                className={`text-left rounded-md p-5 border-2 transition-colors duration-200 ${
                  form.tier === t.id
                    ? t.id === "featured" ? "border-yellow-400 bg-yellow-50/60" : "border-zinc-950 bg-zinc-50"
                    : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
                <div className="flex items-center justify-between">
                  <p className="font-display font-bold text-zinc-950">{t.name}</p>
                  <p className="font-display font-black text-zinc-950">
                    {t.id === "free" ? "Free" : (
                      <>
                        {tierCompare(t.id) && (
                          <span className="text-sm font-semibold text-zinc-400 line-through mr-1.5">£{tierCompare(t.id)}</span>
                        )}
                        £{tierPrice(t.id)}
                      </>
                    )}
                  </p>
                </div>
                <p className="text-xs text-zinc-500 mt-2 leading-relaxed">{t.note}</p>
                {subInfo && ["active", "past_due", "cancelling"].includes(subInfo.status) && (
                  t.id === "standard" ? (
                    (subInfo.credits_remaining || 0) > 0 ? (
                      <p className="mt-2.5 inline-flex text-[11px] font-bold text-green-700 bg-green-100 px-2 py-1 rounded" data-testid="tier-covered-standard">
                        Included in your plan. Uses 1 credit
                      </p>
                    ) : (
                      <p className="mt-2.5 inline-flex text-[11px] font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded" data-testid="tier-covered-standard-none">
                        Plan credits used this month. One-off payment
                      </p>
                    )
                  ) : t.id === "featured" ? (
                    <p className="mt-2.5 inline-flex text-[11px] font-bold text-zinc-600 bg-zinc-100 px-2 py-1 rounded" data-testid="tier-covered-featured">
                      Not included in plans. One-off payment
                    </p>
                  ) : (
                    <p className="mt-2.5 inline-flex text-[11px] font-bold text-zinc-600 bg-zinc-100 px-2 py-1 rounded" data-testid="tier-covered-free">
                      Always free. No plan needed
                    </p>
                  )
                )}
              </button>
            ))}
          </div>
          <label className={`mt-4 flex items-center justify-between gap-4 rounded-md border-2 p-4 cursor-pointer transition-colors duration-200 ${
            form.urgent ? "border-red-400 bg-red-50/50" : "border-zinc-200 hover:border-zinc-300"}`} data-testid="urgent-toggle">
            <div>
              <p className="text-sm font-semibold text-zinc-950">Add the Urgent label {(subInfo?.urgent_credits || 0) > 0
                ? <span className="text-green-700 font-bold">Included. {subInfo.urgent_credits} label{subInfo.urgent_credits === 1 ? "" : "s"} ready</span>
                : <span className="text-red-600 font-bold">+£{urgentPrice}</span>}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{(subInfo?.urgent_credits || 0) > 0
                ? "One of your pre-bought urgent labels will be used for this listing. Nothing added to your total."
                : "A red \"Urgent\" badge and boosted placement. Works on any plan, including free. It's simply added to your total below. One payment at the end, never a separate checkout."}</p>
            </div>
            <input type="checkbox" checked={form.urgent} onChange={(e) => set("urgent", e.target.checked)}
              className="h-5 w-5 accent-red-600" data-testid="urgent-checkbox" />
          </label>
          {(form.tier !== "free" || form.urgent) && (
            <div className="mt-4 rounded-md border-2 border-zinc-200 p-4" data-testid="location-boost">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-zinc-950">Location Boost <span className="text-xs font-medium text-blue-700 ml-1">Included with your listing</span></p>
                  <p className="text-xs text-zinc-500 mt-0.5">Show this job to candidates searching up to 10 nearby towns and areas.</p>
                </div>
                <button type="button" onClick={suggestBoost} disabled={!form.location.trim() || boostLoading} data-testid="boost-suggest"
                  className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 whitespace-nowrap transition-colors duration-200">
                  {boostLoading ? "Finding…" : "Suggest nearby areas"}
                </button>
              </div>
              {form.boost_locations.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="boost-chips">
                  {form.boost_locations.map((l) => (
                    <span key={l} className="inline-flex items-center gap-1.5 bg-zinc-100 text-zinc-800 text-xs font-medium px-2.5 py-1.5 rounded-full"
                      data-testid={`boost-chip-${l.replace(/\s+/g, "-").toLowerCase()}`}>
                      {l}
                      <button type="button" onClick={() => set("boost_locations", form.boost_locations.filter((x) => x !== l))}
                        aria-label={`Remove ${l}`} data-testid={`boost-remove-${l.replace(/\s+/g, "-").toLowerCase()}`}
                        className="text-zinc-400 hover:text-red-600 transition-colors duration-200">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <input value={boostInput} onChange={(e) => setBoostInput(e.target.value)} data-testid="boost-custom-input"
                  placeholder="Add an area yourself (e.g. Dagenham)" maxLength={60}
                  className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <button type="button" onClick={addBoost} disabled={!boostInput.trim()} data-testid="boost-add"
                  className="h-10 px-4 border border-zinc-300 text-xs font-semibold rounded-md text-zinc-700 hover:border-zinc-500 disabled:opacity-50 transition-colors duration-200">
                  Add
                </button>
              </div>
              <p className="text-xs text-zinc-400 mt-2">Your job still shows one location on the page. Boost simply makes it appear in searches for the areas you pick.</p>
            </div>
          )}
          {(form.tier !== "free" || form.urgent) && (
            <div className="mt-4">
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Promo code (optional)</label>
              <div className="flex gap-2">
                <input data-testid="promo-code-input" value={promoCode}
                  onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromo(null); setPromoError(""); }}
                  placeholder="e.g. LAUNCH25"
                  className="flex-1 h-12 px-4 border border-zinc-300 rounded-md text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <button type="button" onClick={applyPromo} disabled={promoChecking || !promoCode.trim()} data-testid="promo-apply"
                  className="h-12 px-6 border border-zinc-300 text-sm font-semibold rounded-md text-zinc-700 hover:border-zinc-500 transition-colors duration-200 disabled:opacity-50">
                  {promoChecking ? "Checking…" : "Apply"}
                </button>
              </div>
              {promo && <p className="text-xs font-medium text-green-700 mt-1.5" data-testid="promo-applied">{promo.code}: {promo.percent}% off applied.</p>}
              {promoError && <p className="text-xs font-medium text-red-600 mt-1.5" data-testid="promo-error">{promoError}</p>}
            </div>
          )}
        </section>

        <label className="flex items-start gap-3 border border-zinc-200 rounded-md p-4 bg-white cursor-pointer" data-testid="partner-distribution-row">
          <input type="checkbox" checked={!!form.partner_distribution} data-testid="partner-distribution-checkbox"
            onChange={(e) => set("partner_distribution", e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-blue-700 shrink-0" />
          <span>
            <span className="block text-sm font-semibold text-zinc-900">Distribute this vacancy across HiredToday partner job boards</span>
            <span className="block text-xs text-zinc-500 mt-1">Allow HiredToday to share this vacancy with approved recruitment partners to increase its visibility. Applications may still be managed through HiredToday.</span>
          </span>
        </label>

        <div className="flex items-start gap-2 text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2.5 mb-4" data-testid="content-rules-hint">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-zinc-400" />
          <p>
            Listings are checked automatically before submission. Please don't include phone numbers, email addresses
            or links (candidates apply through HiredToday), any charge or fee to applicants, or wording that favours or
            excludes anyone by age, sex, race, religion, disability or any other protected characteristic.{" "}
            <span className="group relative inline-flex">
              <span className="underline decoration-dotted cursor-help" data-testid="content-rules-why">Why?</span>
              <span className="pointer-events-none absolute bottom-full left-0 z-20 mb-1.5 w-64 rounded-md bg-zinc-950 px-3 py-2 text-[11px] leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                UK law (the Equality Act 2010 and the Employment Agencies Act) bans discriminatory job adverts and
                charging jobseekers fees. Listings breaking these rules are rejected and may be reported.
              </span>
            </span>
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button type="submit" disabled={submitting} data-testid="submit-job-button"
            className="group inline-flex items-center justify-center gap-2 h-13 px-10 py-4 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
            {submitting ? "Working on it…" : fullyCovered
              ? (planUsed ? "Submit for review · covered by your plan" : "Submit for review")
              : `Continue to payment (£${duePrice % 1 === 0 ? duePrice : duePrice.toFixed(2)})`}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <button type="button" onClick={saveTemplate} data-testid="save-template-button"
            className="inline-flex items-center justify-center gap-2 h-13 px-6 py-4 border border-zinc-300 text-sm font-semibold rounded-md text-zinc-700 hover:border-zinc-500 transition-colors duration-200">
            <Bookmark className="h-4 w-4" /> Save as template
          </button>
        </div>
      </form>
    </main>
  );
}

const POST_JOB_ROLES = ["employer", "admin"];

export default function PostJob() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="post-job-page">
        <Seo title="Post a Job Free in the UK | HiredToday" description="Post a UK job free, or choose Standard or Featured for more reach." path="/post-a-job" />
        <div className="min-h-[40vh] flex items-center justify-center">
          <div className="h-8 w-8 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
        </div>
      </main>
    );
  }
  if (user === false) {
    return (
      <main className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="post-job-page">
        <PageGlow subtle />
        <Seo title="Post a Job Free in the UK | HiredToday" description="Post a UK job free, or choose Standard or Featured for more reach." path="/post-a-job" />
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">Employers</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-black text-zinc-950">Post a job free</h1>
        <p className="mt-4 text-zinc-600 leading-relaxed max-w-xl">
          Reach UK jobseekers and manage every application in one place.
        </p>
        <div className="mt-8 glass rounded-lg p-6 sm:p-8 max-w-xl" data-testid="postjob-gate">
          <p className="font-display text-lg font-bold text-zinc-950">Log in or create an employer account to post</p>
          <p className="mt-2 text-sm text-zinc-600 leading-relaxed">It takes a couple of minutes. Your first listing is free.</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Link to="/register?role=employer&next=/post-a-job" data-testid="postjob-register"
              className="bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-zinc-800 transition-colors duration-200 text-center">
              Create an employer account
            </Link>
            <Link to="/login?next=/post-a-job" data-testid="postjob-login"
              className="border border-zinc-300 text-sm font-semibold px-6 py-3 rounded-md hover:border-zinc-500 transition-colors duration-200 text-center">
              Log in
            </Link>
          </div>
        </div>
      </main>
    );
  }
  return (
    <ProtectedRoute roles={POST_JOB_ROLES}>
      <PostJobForm />
    </ProtectedRoute>
  );
}
